/**
 * PATH: apps/quote/src/components/builder/RightEdgeRail.tsx
 * PURPOSE: Always-visible right edge rail for quick access to right drawer tabs
 *
 * WHY:
 * - Right drawer is powerful but hidden by default
 * - Users need a discoverable way to access Inspector/Context/AI/Claims/History
 * - Icons on the right edge provide always-visible entry points
 *
 * FLOW:
 *   Show rail → Click icon → Open drawer to that tab → Active state highlights
 */

import {
  Info,
  FileText,
  Sparkles,
  Receipt,
  Clock,
  Camera,
  Pin,
  PinOff,
  BarChart3,
  Hourglass,
  Package,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkspace, type RightDrawerTab } from './BuilderWorkspace'

interface RailItem {
  id: RightDrawerTab
  icon: React.ElementType
  label: string
  shortcut?: string
  description: string
}

const RAIL_ITEMS: RailItem[] = [
  {
    id: 'inspector',
    icon: Info,
    label: 'Inspector',
    shortcut: 'I',
    description: 'View and edit selected item details',
  },
  {
    id: 'kpi',
    icon: BarChart3,
    label: 'KPI Summary',
    shortcut: 'K',
    description: 'Per-discipline cost / charge-out / margin breakdown',
  },
  {
    id: 'wages',
    icon: Hourglass,
    label: 'Wages Build Up',
    shortcut: 'W',
    description: 'Normal/overtime hours, escalation, final charge-out rate',
  },
  {
    id: 'products',
    icon: Package,
    label: 'Product Summary',
    shortcut: 'P',
    description: 'Material take-off matrix by section',
  },
  {
    id: 'context',
    icon: FileText,
    label: 'Context',
    shortcut: 'C',
    description: 'Files, drawings, and references',
  },
  {
    id: 'ai',
    icon: Sparkles,
    label: 'AI Assist',
    shortcut: 'A',
    description: 'AI suggestions and insights',
  },
  {
    id: 'claims',
    icon: Receipt,
    label: 'Claims',
    description: 'Payment claims for this quote',
  },
  {
    id: 'snapshots',
    icon: Camera,
    label: 'Snapshots',
    shortcut: 'S',
    description: 'Capture point-in-time snapshots before destructive edits',
  },
  {
    id: 'history',
    icon: Clock,
    label: 'History',
    shortcut: 'H',
    description: 'Granular row-level change log',
  },
]

function RailButton({
  item,
  isActive,
  onClick,
}: {
  item: RailItem
  isActive: boolean
  onClick: () => void
}) {
  const Icon = item.icon

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative w-10 h-10 flex items-center justify-center rounded-lg transition-all group',
        isActive
          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
          : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300'
      )}
      title={`${item.label}${item.shortcut ? ` (${item.shortcut})` : ''}`}
    >
      <Icon className="w-5 h-5" />
      
      {/* Active indicator */}
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-blue-600 dark:bg-blue-400 rounded-r" />
      )}
      
      {/* Tooltip */}
      <div className="absolute right-full mr-2 px-2 py-1 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
        {item.label}
        {item.shortcut && (
          <span className="ml-2 text-slate-400 dark:text-slate-500">{item.shortcut}</span>
        )}
      </div>
    </button>
  )
}

export default function RightEdgeRail() {
  const {
    rightDrawerOpen,
    rightDrawerTab,
    rightDrawerPinned,
    setRightDrawerTab,
    openRightDrawer,
    closeRightDrawer,
    toggleRightDrawerPin,
  } = useWorkspace()

  const handleClick = (tabId: RightDrawerTab) => {
    if (rightDrawerOpen && rightDrawerTab === tabId) {
      // Clicking active tab toggles drawer (unless pinned)
      if (!rightDrawerPinned) {
        closeRightDrawer()
      }
    } else {
      setRightDrawerTab(tabId)
    }
  }

  return (
    <div className="flex-shrink-0 w-12 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 flex flex-col items-center py-2 gap-1">
      {/* Tab buttons */}
      {RAIL_ITEMS.map((item) => (
        <RailButton
          key={item.id}
          item={item}
          isActive={rightDrawerOpen && rightDrawerTab === item.id}
          onClick={() => handleClick(item.id)}
        />
      ))}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Pin toggle */}
      {rightDrawerOpen && (
        <button
          onClick={toggleRightDrawerPin}
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-lg transition-colors',
            rightDrawerPinned
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
              : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
          )}
          title={rightDrawerPinned ? 'Unpin drawer' : 'Pin drawer open'}
        >
          {rightDrawerPinned ? (
            <PinOff className="w-4 h-4" />
          ) : (
            <Pin className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  )
}

